"use client";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { ChevronDownIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Dispatch, SetStateAction, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import Loading from "../../ui/loading";
import { registerWithEmail } from "@/services/auth";
import Link from "next/link";

export default function RegisterSection() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !password.trim() || !date) {
      setError("All fields are required");
      return;
    }

    if (name.trim().length < 5) {
      setError("Name must be at least 5 characters long");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    const today = new Date();
    const age = today.getFullYear() - date.getFullYear();
    const hasBirthdayPassed =
      today.getMonth() > date.getMonth() ||
      (today.getMonth() === date.getMonth() &&
        today.getDate() >= date.getDate());

    const actualAge = hasBirthdayPassed ? age : age - 1;

    if (actualAge < 12) {
      setError("You must be at least 12 years old to register");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await registerWithEmail(email, password, { name, dob: date });
      router.replace("/login");
    } catch (err: any) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="overflow-hidden h-screen flex justify-center items-center p-6">
      {loading ? <Loading /> : null}
      <Image
        src={"/assets/logo.png"}
        alt=""
        width={30}
        height={30}
        className="opacity-75 absolute top-7"
      />
      <form
        onSubmit={onSubmit}
        className="w-full flex justify-center items-center"
      >
        <Card className="w-full sm:max-w-md bg-zinc-800/30 border-none backdrop-blur-sm shadow-2xl text-gray-300 relative">
          <CardHeader>
            <CardTitle className="text-xl">Register an account</CardTitle>
            <CardDescription>
              Enter your details below to register a new account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  className="px-2"
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="name">Date Of Birth</Label>
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      id="date"
                      className={`${
                        open ? "border-gray-300" : "border-zinc-700"
                      } justify-between font-normal border-b rounded-sm w-full px-2`}
                    >
                      {date ? date.toLocaleDateString() : "Select date"}
                      <ChevronDownIcon />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto overflow-hidden p-0 border-none"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={date}
                      captionLayout="dropdown"
                      onSelect={(date) => {
                        setDate(date);
                        setOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  className="px-2"
                  id="email"
                  type="email"
                  placeholder="johndoe@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  className="px-2"
                  id="password"
                  type="password"
                  required
                  placeholder="* * * * * * * *"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex-col gap-2 mt-2">
            {error && (
              <p className="text-red-500 italic px-1 mb-2 -mt-4 text-start w-full">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-400 hover:bg-zinc-200 text-gray-800 hover:text-gray-900"
            >
              {loading ? "Loading..." : "Register"}
            </Button>
            <Link
              href={"/login"}
              className="w-fit text-gray-400 hover:text-gray-100"
            >
              Already have an account? Log in
            </Link>
          </CardFooter>
        </Card>
      </form>
    </main>
  );
}
